import { useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { usePersistentNotices } from '../components/notifications/notificationStore'
import type { PersistentNotice } from '../components/notifications/notificationStore'
import { swUpdateStore } from './swUpdate'

/**
 * LOT-SW-REPOS — « Une mise à jour est prête — elle s'installera à la fin de
 * l'appel ».
 *
 * Rien n'est affiché au repos : la mise à jour s'applique alors d'elle-même,
 * en silence. Le bandeau n'existe que quand une mise à jour ATTEND parce que
 * le poste est occupé (`status === 'deferred'`), et il disparaît de lui-même
 * dès qu'elle s'applique.
 *
 * Il passe par le registre des états persistants de LOT-H9 (`usePersistentNotices`)
 * — donc par la zone de notification de l'`AppShell`, DANS le flux, sous
 * l'en-tête : il ne recouvre rien, ne vole aucun focus, n'a aucun bouton (il
 * n'y a rien à faire — c'est précisément le message). Ton `warning` = « à
 * surveiller, rien de cassé » (DESIGN-SYSTEM §1.2), porté par le kit (`Alert`),
 * jamais par une couleur en dur.
 *
 * Hors châssis (`/login`, `/register`) aucune zone ne rend les avis : c'est
 * voulu — il n'y a pas d'appel possible sur ces écrans, donc jamais de différé.
 */
export const SW_UPDATE_NOTICE_ID = 'pwa:update-deferred'

export function ServiceWorkerUpdateBanner() {
  const { t } = useTranslation('shell')
  const { status } = useSyncExternalStore(
    swUpdateStore.subscribe,
    swUpdateStore.getSnapshot,
    swUpdateStore.getSnapshot,
  )

  const notices = useMemo<PersistentNotice[]>(
    () =>
      status === 'deferred'
        ? [
            {
              id: SW_UPDATE_NOTICE_ID,
              tone: 'warning',
              title: t('pwaUpdate.title'),
              description: t('pwaUpdate.description'),
              // La condition est binaire (une MAJ attend / n'attend pas) :
              // une seule empreinte suffit — fermé, le bandeau reste fermé
              // tant que la même attente dure.
              signature: 'deferred',
            },
          ]
        : [],
    [status, t],
  )

  usePersistentNotices('pwa:update', notices)

  return null
}

export default ServiceWorkerUpdateBanner
